import { useDispatch, useSelector } from "react-redux";
import { AcronymReducerState } from "../../../interfaces/store/acronymReducerState";
import { useEffect, useState, forwardRef, useImperativeHandle } from "react";
import { useNavigate } from "react-router-dom";
import CustomTable from "../../common/ui/CustomDataGridTable";
import {
  Box,
  IconButton,
} from "@mui/material";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { GridRenderCellParams } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import { actionTypes } from "../../../store/actionTypes/actionTypes";
import { ADMIN } from "../../../constants/admin";
import {
  deleteDataFromApi,
  postDataFromApi,
} from "../../../services/api/apiService";
import { RestfulUrls } from "../../../constants/restfulUrls";
import { IApiResponse } from "../../../interfaces/common/IApiResponse";
import { COMMON } from "../../../constants/common";
import LoaderWithText from "../../common/ui/LoaderWithText";
import { UserPermission } from "../../common/UserPermission";


interface AcronymTableProps {
}

const AcronymTable = forwardRef<any, AcronymTableProps>(({}, ref) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [totalCount, setTotalCount] = useState<number>(0);
  const [selectedRowData, setSelectedRowData] = useState<{ id: string } | null>(
    null
  );
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [disableDeleteSubmitBtn, setDisableDeleteSubmitBtn] = useState(false);

  const fetchAcronym = useSelector(
    (state: { acronym: AcronymReducerState }) =>
      state.acronym.fetchAcronym
  );

  const [rowData, setRowData] = useState<any>([]);

  const getAcronymDetails = async (
    page: number,
    limit: number,
    search: string = ""
  ) => {
    try {
      setLoading(true);
      const url = `${RestfulUrls.BASE_URL}${RestfulUrls.PAGINATED_ACRONYMS}`;
      const payloadData = {
        page: page,
        limit: limit,
        search: search,
      };
      await postDataFromApi<any>(
        url,
        payloadData,
        (response: IApiResponse<any>) => {
          console.log("get ACRONYM response data ", response);
          dispatch({
            type: actionTypes.UPDATE_ACRONYM_TABLE_DATA,
            payload: response.data.data,
          });
          setTotalCount(response.data.pagination.total);

          setRowData(response.data.data);
        },
        { isAuthRequired: true },
        navigate
      );
    } catch (error) {
      console.error("Error fetching acronyms:", error);
      dispatch({
        type: COMMON.SHOW_SNACKBAR,
        payload: {
          message: "Error fetching acronyms.",
          severity: COMMON.SNACKBAR_SEVERITY.ERROR,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
    getAcronymDetails(newPage, pageSize, searchTerm);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
  };

  const handleAddButtonClick = () => {
    dispatch({
      type: actionTypes.SELECTED_ACRONYM_TABLE_ROW,
      payload: null,
    });
    dispatch({
      type: actionTypes.OPEN_ACRONYM_FORM,
      payload: true,
    });
    dispatch({
      type: actionTypes.UPDATE_FETCH_ACRONYM,
      payload: false,
    });
  };


  const onEditIconClick = (selectedRow: any) => {
    dispatch({
      type: actionTypes.OPEN_ACRONYM_FORM,
      payload: true,
    });
    dispatch({
      type: actionTypes.SELECTED_ACRONYM_TABLE_ROW,
      payload: selectedRow,
    });
    dispatch({
      type: actionTypes.UPDATE_ACRONYM_FORM_TYPE,
      payload: ADMIN.MEETING_FORM_TYPE.EDIT,
    });
    dispatch({
      type: actionTypes.UPDATE_FETCH_ACRONYM,
      payload: false,
    });
  };

  const dummyModalCloseTw = () => {};

  const handleDeleteAcronym = async () => {
    setDisableDeleteSubmitBtn(true);
    const url = `${RestfulUrls.BASE_URL}${RestfulUrls.DELETE_ACRONYM}/${selectedRowData?.id}`;
    await deleteDataFromApi<any>(
      url,
      null,
      () => {
        dispatch({
          type: COMMON.SHOW_SNACKBAR,
          payload: {
            message: "Acronym deleted successfully.",
            severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
          },
        });
        getAcronymDetails(page, pageSize, searchTerm);
        setIsDeleteModalOpen(false);
        setDisableDeleteSubmitBtn(false);
      },
      { isAuthRequired: true },
      navigate
    );
    setDisableDeleteSubmitBtn(false);
  };

  const onSelectedDeleteIconClick = (selectedRow: any) => {
    setIsDeleteModalOpen(true);
    setSelectedRowData(selectedRow);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedRowData(null);
  };

  const tableHeader = [
    {
      field: "id",
      headerName: <div className='fw-700'>ID</div>,
      width: 60,
      sortable: false,
    },
    {
      field: "acronym",
      headerName: <div className='fw-700'>Acronym</div>,
      flex: 0.7,
      minWidth: 120,
    },
    {
      field: "fullForm",
      headerName: <div className='fw-700'>Full Form</div>,
      flex: 1.3,
      minWidth: 200,
    },
    {
      field: "actions",
      headerName: <div className='fw-700'>Actions</div>,
      flex: 0.6,
      minWidth: 100,
      resizable: false,
      renderCell: (params: GridRenderCellParams) => {
        return (
          <Box sx={{ display: "flex", gap: 1 }}>
            <Tooltip title='Edit Acronym'>
              <IconButton
                size='small'
                onClick={() => {
                  onEditIconClick(params.row);
                }}
              >
                <EditOutlinedIcon className='de-icon' />
              </IconButton>
            </Tooltip>
            <Tooltip title='Delete Acronym'>
              <IconButton
                size='small'
                onClick={() => onSelectedDeleteIconClick(params.row)}
              >
                <DeleteOutlineOutlinedIcon className='de-icon' />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  useEffect(() => {
    getAcronymDetails(page, pageSize, searchTerm);
  }, [page, pageSize]);

  useEffect(() => {
    if (fetchAcronym) {
      getAcronymDetails(page, pageSize, searchTerm);
    }
  }, [fetchAcronym]);

  useImperativeHandle(ref, () => ({
    performSearch: (query: string) => {
      setSearchTerm(query);
      setPage(1);
      getAcronymDetails(1, pageSize, query);
    },
    performCreate: () => {
      handleAddButtonClick();
    },
  }));

  return (
    <>
      <Box className='acronym-table'>
        <CustomTable
          columns={tableHeader}
          rows={rowData}
          hideFooter={false}
          disableColumnFilter={false}
          checkboxSelection={false}
          disableColumnSelector={true}
          disableDensitySelector={true}
          className='acronym_datatable'
          showToolbar={false}
          paginationMode='server'
          pageSize={pageSize}
          page={page - 1} // MUI DataGrid uses 0-based pagination
          rowCount={totalCount}
          onPageChange={(newPage) => handlePageChange(newPage + 1)}
          onPageSizeChange={handlePageSizeChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          disableColumnSorting={true}
        />
      </Box>
      <LoaderWithText isOpen={loading} onClose={dummyModalCloseTw} />
      <UserPermission
        isModalOpen={isDeleteModalOpen}
        titleOne={"Are you sure"}
        title={"you want to delete selected item?"}
        disableSubmitBtn={disableDeleteSubmitBtn}
        onConfirmedButtonClick={handleDeleteAcronym}
        onCancelButtonClick={handleCloseDeleteModal}
        onCloseModal={handleCloseDeleteModal}
      />
    </>
  );
});

AcronymTable.displayName = "AcronymTable";

export default AcronymTable;

